import PropTypes from 'prop-types';
import { FC, ReactNode, useEffect, useState } from 'react';
import { Navigate, json, useLocation } from 'react-router-dom';
import Login from '../features/authentication/Login';
import useAuth from '../hooks/useAuth';

interface AuthGuardProps {
  children: ReactNode;
}

const isBonsaiUser = (roles: string[]) => {
  // 速度を考えてforを使用
  for (const r of roles) {
    if (r.startsWith('bonsai-')) {
      return true;
    }
  }
  return false;
};

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props;
  const auth = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);

  const currentPathName: string = location.pathname + location.search;

  // MFA対応 start --------------
  // MFA必須のユーザーがMFA未認証の場合 (※ID/Passwordによる1次認証済み)
  useEffect(() => {
    const executeLogout = async () => {
      await auth.logout();
    };
    // MFA必須のユーザーがMFA未認証の場合、1次認証時にログイン済みなので、ログアウトしてログイン画面へ遷移させる
    if (auth.isRequiredMfa && !auth.isMfaAuthenticated) {
      executeLogout();
    }
  }, [auth.isRequiredMfa, auth.isMfaAuthenticated]);

  // ログイン未完了の場合
  if (!auth.isAuthenticated) {
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }
    return <Login />;
  }
  // MFA対応 end ----------------

  if (!auth.isAuthenticated) {
    if (currentPathName !== requestedLocation) {
      setRequestedLocation(currentPathName);
    }

    return <Login />;
  }

  if (auth.method.toUpperCase() === 'COGNITO') {
    if (auth.user && auth.isAuthenticated && !isBonsaiUser(auth.user.role)) {
      throw json(null, { status: 403, statusText: 'You are not allowed to access this page' });
    }
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (requestedLocation && currentPathName !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
};

export default AuthGuard;
